import React, { useEffect, useState } from 'react';
import './CustomSearchControl.scss';
import clsx from 'clsx';
import { debounce } from 'lodash';

interface SearchProps {
  // eslint-disable-next-line no-unused-vars
  onSearch?: (value: string) => void;
  defaultValue?: string;
  debounceTime?: number;
  // eslint-disable-next-line no-unused-vars
  searchSchema?: (searchTerm: string) => string;
  placeHolder?: string;
  className?: string;
}

const CustomSearchControl = ({
  onSearch,
  defaultValue,
  debounceTime = 300,
  searchSchema,
  placeHolder,
  className,
}: SearchProps) => {
  const [searchValue, setSearchValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');

  const debouncedSearch = React.useCallback(
    debounce(async (query: string) => {
      let validationMessage = '';
      validationMessage = searchSchema && searchSchema(query);
      if (validationMessage === '' || validationMessage === undefined) {
        onSearch && onSearch(query);
      }
      setError(validationMessage);
    }, debounceTime),
    []
  );

  // Cancel debounced search on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const searchHandler = (event: { target: { value: any } }) => {
    const searchTerm = event.target.value;
    setSearchValue(searchTerm);
    debouncedSearch(searchTerm);
  };

  const clearhandler = () => {
    setSearchValue('');
    setError('');
    onSearch && onSearch('');
    searchHandler({ target: { value: '' } });
  };

  useEffect(() => {
    setSearchValue(defaultValue);
  }, [defaultValue]);

  const hidePlaceholder = isFocused || searchValue;

  return (
    <div className={clsx('custom-search', className)}>
      <div className="relative w-full h-full">
        <div
          className={clsx(
            hidePlaceholder && 'opacity-0',
            'absolute flex items-center w-full justify-center h-full pointer-events-none transition-opacity gap-xxs',
            'text-ampol-black'
          )}
        >
          <div className="search-icon" />
          <p>{placeHolder ?? 'Search'}</p>
        </div>

        <div
          className={clsx(
            'absolute p-[12px] flex items-center w-full justify-end h-full pointer-events-none gap-xxs'
          )}
        >
          <div
            className={clsx(
              'absolute search-icon',
              (searchValue || !isFocused) && 'opacity-0'
            )}
            onClick={clearhandler}
          />
          <div
            className={clsx(
              'absolute clear-icon pointer-events-auto',
              !searchValue && 'opacity-0'
            )}
            onClick={clearhandler}
          />
        </div>

        <input
          name="searchTerm"
          type="text"
          value={searchValue}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={searchHandler}
          className="w-full h-full py-[4px] px-[12px] bg-white outline-none border-none rounded-[2px]"
        />
      </div>
    </div>
  );
};

export default CustomSearchControl;
